import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComingSoon } from '../components/ComingSoon.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';
import { DeepDiveTab } from '../components/funnels/DeepDiveTab.js';
import { DiagnosisFunnelTab } from '../components/funnels/DiagnosisFunnelTab.js';
import { DimensionsTab } from '../components/funnels/DimensionsTab.js';
import { FUNNEL_NAMES } from '../components/funnels/constants.js';
import { FunnelKpiCards } from '../components/funnels/FunnelKpiCards.js';
import { FunnelTab } from '../components/funnels/FunnelTab.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { funnelsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル詳細分析。ステップ別ユーザー数/コンバージョン率/ドロップオフ率・滞在時間・ディメンション別内訳を可視化
 * @graph-connects funnelsApi [calls] Funnels APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects ComingSoon [calls] Coming Soonオーバーレイ描画
 * @graph-connects FunnelKpiCards [calls] ファネルKPIカード描画
 * @graph-connects FunnelTab [calls] ファネルタブ描画
 * @graph-connects DiagnosisFunnelTab [calls] 診断ファネルタブ描画
 * @graph-connects DimensionsTab [calls] ディメンションタブ描画
 * @graph-connects DeepDiveTab [calls] 深掘り分析タブ描画
 */
export const Route = createFileRoute('/funnels/$funnelId')({
  component: FunnelDetailPage,
});

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function FunnelDetailPage() {
  const { t, i18n } = useTranslation();
  const { funnelId } = useParams({ from: '/funnels/$funnelId' });
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');
  const [activeTab, setActiveTab] = useState<'funnel' | 'dimensions' | 'deepdive'>('funnel');

  const lang = i18n.language === 'en' ? 'en' : 'ja';
  const funnelLabel = FUNNEL_NAMES[funnelId][lang];

  // ファネル定義取得（ディメンション一覧用）
  const listQuery = useQuery({
    queryKey: queryKeys.funnels.list(),
    queryFn: () => funnelsApi.getList(),
    staleTime: 30 * 60 * 1000,
  });

  const funnelDef = listQuery.data?.data.find((f) => f.id === funnelId);

  // ファネルデータ取得
  const detailQuery = useQuery({
    queryKey: queryKeys.funnels.detail(funnelId, startDate, endDate),
    queryFn: () => funnelsApi.getDetail(funnelId, startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const content = (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/funnels"
            className="text-sm text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)] transition-colors"
          >
            ← {t('funnels.title')}
          </Link>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">
            {funnelLabel}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">{funnelDef?.description ?? ''}</p>
        </div>
        <DateRangeSelector
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onPresetChange={setPreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* KPI カード */}
      {detailQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-shimmer h-24 rounded-lg" />
          ))}
        </div>
      ) : detailQuery.data ? (
        <FunnelKpiCards data={detailQuery.data} />
      ) : null}

      {/* タブ */}
      <div className="flex gap-1 p-1 rounded-lg bg-[var(--color-bg-hover)] w-fit">
        {(
          [
            { key: 'funnel' as const, label: 'ファネル' },
            { key: 'dimensions' as const, label: 'ディメンション' },
            ...((funnelDef?.analysisTypes.length ?? 0) > 0
              ? [{ key: 'deepdive' as const, label: '深掘り分析' }]
              : []),
          ] as { key: typeof activeTab; label: string }[]
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
            }}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-[var(--color-accent-primary)] text-white shadow-sm'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      {activeTab === 'funnel' ? (
        funnelId === 'diagnosis' ? (
          <DiagnosisFunnelTab detailQuery={detailQuery} />
        ) : (
          <FunnelTab detailQuery={detailQuery} />
        )
      ) : activeTab === 'dimensions' ? (
        <DimensionsTab
          funnelId={funnelId}
          funnelDef={funnelDef}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <DeepDiveTab
          funnelId={funnelId}
          funnelDef={funnelDef}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </div>
  );

  const enabledFunnels = ['registration', 'purchase'];
  if (!enabledFunnels.includes(funnelId)) {
    return <ComingSoon>{content}</ComingSoon>;
  }

  return content;
}
